//#region src/result.ts
/**
* Creates a successful Result containing the given value.
*
* @template T - The type of the success value
* @param value - The value to wrap in an Ok
* @returns An Ok containing the value
*
* @example
* ```typescript
* const result = ok(42)
* // result.ok === true
* // result.value === 42
* ```
*/
function ok(value) {
	return {
		ok: true,
		value
	};
}
/**
* Creates a failed Result containing the given error.
*
* @template E - The type of the error value
* @param error - The error to wrap in an Err
* @returns An Err containing the error
*
* @example
* ```typescript
* const result = err('Something went wrong')
* // result.ok === false
* // result.error === 'Something went wrong'
* ```
*/
function err(error) {
	return {
		ok: false,
		error
	};
}
/**
* Type guard that narrows a Result to Ok.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to check
* @returns `true` if the Result is Ok, enabling type narrowing
*
* @example
* ```typescript
* const result: Result<number, string> = ok(42)
*
* if (isOk(result)) {
*   // TypeScript knows result.value exists here
*   console.log(result.value)
* }
* ```
*/
function isOk(result) {
	return result.ok;
}
/**
* Type guard that narrows a Result to Err.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to check
* @returns `true` if the Result is Err, enabling type narrowing
*
* @example
* ```typescript
* const result: Result<number, string> = err('failed')
*
* if (isErr(result)) {
*   // TypeScript knows result.error exists here
*   console.error(result.error)
* }
* ```
*/
function isErr(result) {
	return !result.ok;
}
/**
* Extracts the value from an Ok Result or throws an error if Err.
*
* **Warning**: This function throws. Prefer `unwrapOr`, `unwrapOrElse`, or `match`
* for safer error handling.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to unwrap
* @returns The contained value if Ok
* @throws Error with message from ResultError or stringified error if Err
*
* @example
* ```typescript
* const success = ok(42)
* unwrap(success) // Returns 42
*
* const failure = err('oops')
* unwrap(failure) // Throws Error: 'Unwrap called on Err: oops'
* ```
*/
function unwrap(result) {
	if (isOk(result)) return result.value;
	const errMsg = result.error instanceof Object && "message" in result.error ? result.error.message : String(result.error);
	throw new Error(`Unwrap called on Err: ${errMsg}`);
}
/**
* Extracts the value from an Ok Result or returns a default value if Err.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to unwrap
* @param defaultValue - The value to return if the Result is Err
* @returns The contained value if Ok, otherwise the default value
*
* @example
* ```typescript
* unwrapOr(ok(42), 0)      // Returns 42
* unwrapOr(err('oops'), 0) // Returns 0
* ```
*/
function unwrapOr(result, defaultValue) {
	return isOk(result) ? result.value : defaultValue;
}
/**
* Extracts the value from an Ok Result or computes a value from the error if Err.
*
* Unlike `unwrapOr`, the default value is lazily computed only when needed.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to unwrap
* @param fn - Function to compute the default value from the error
* @returns The contained value if Ok, otherwise the computed value
*
* @example
* ```typescript
* unwrapOrElse(ok(42), () => 0)                    // Returns 42
* unwrapOrElse(err('error'), (e) => e.length)      // Returns 5
* unwrapOrElse(err('not found'), () => fetchDefault()) // Only fetches on error
* ```
*/
function unwrapOrElse(result, fn) {
	return isOk(result) ? result.value : fn(result.error);
}
/**
* Transforms the Ok value using the provided function, leaving Err unchanged.
*
* This is the functor map operation for Result, allowing you to transform
* success values while automatically propagating errors.
*
* @template T - The type of the input success value
* @template U - The type of the output success value
* @template E - The type of the error value
* @param result - The Result to transform
* @param fn - Function to apply to the Ok value
* @returns A new Result with the transformed value, or the original Err
*
* @example
* ```typescript
* map(ok(2), x => x * 2)           // ok(4)
* map(err('oops'), x => x * 2)     // err('oops')
*
* // Chaining transformations
* const result = ok('hello')
* const upper = map(result, s => s.toUpperCase())
* const length = map(upper, s => s.length)
* // length = ok(5)
* ```
*/
function map(result, fn) {
	return isOk(result) ? ok(fn(result.value)) : result;
}
/**
* Transforms the Err value using the provided function, leaving Ok unchanged.
*
* Useful for converting error types or adding context to errors.
*
* @template T - The type of the success value
* @template E - The type of the input error value
* @template F - The type of the output error value
* @param result - The Result to transform
* @param fn - Function to apply to the Err value
* @returns A new Result with the transformed error, or the original Ok
*
* @example
* ```typescript
* mapErr(err('oops'), e => e.toUpperCase())  // err('OOPS')
* mapErr(ok(42), e => e.toUpperCase())       // ok(42)
*
* // Converting error types
* const result = err('not found')
* const typed = mapErr(result, msg => ({ code: 'NOT_FOUND', message: msg }))
* ```
*/
function mapErr(result, fn) {
	return isErr(result) ? err(fn(result.error)) : result;
}
/**
* Chains a function that returns a Result over the Ok value (monadic bind).
*
* Unlike `map`, the function returns a Result, which is then flattened.
* This enables composing operations that may fail.
*
* @template T - The type of the input success value
* @template U - The type of the output success value
* @template E - The type of the error value
* @param result - The Result to chain
* @param fn - Function that takes the Ok value and returns a new Result
* @returns The Result from fn if input is Ok, otherwise the original Err
*
* @example
* ```typescript
* const parseNumber = (s: string): Result<number, string> => {
*   const n = parseInt(s, 10)
*   return isNaN(n) ? err('not a number') : ok(n)
* }
*
* const divide = (a: number, b: number): Result<number, string> =>
*   b === 0 ? err('division by zero') : ok(a / b)
*
* // Chain operations that may fail
* const result = flatMap(parseNumber('10'), n => divide(n, 2))
* // result = ok(5)
*
* const failed = flatMap(parseNumber('abc'), n => divide(n, 2))
* // failed = err('not a number')
* ```
*/
function flatMap(result, fn) {
	return isOk(result) ? fn(result.value) : result;
}
/**
* Executes a side effect on the Ok value without changing the Result.
*
* Useful for logging, debugging, or other side effects in a pipeline.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to tap
* @param fn - Side effect function to execute on Ok value
* @returns The original Result unchanged
*
* @example
* ```typescript
* const result = ok(42)
* const same = tap(result, v => console.log('Got:', v))
* // Logs: 'Got: 42'
* // same === result
*
* tap(err('oops'), v => console.log('Got:', v))
* // Does not log anything
* ```
*/
function tap(result, fn) {
	if (isOk(result)) fn(result.value);
	return result;
}
/**
* Executes a side effect on the Err value without changing the Result.
*
* Useful for logging errors or triggering error reporting.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @param result - The Result to tap
* @param fn - Side effect function to execute on Err value
* @returns The original Result unchanged
*
* @example
* ```typescript
* const result = err('oops')
* const same = tapErr(result, e => console.error('Error:', e))
* // Logs: 'Error: oops'
* // same === result
*
* tapErr(ok(42), e => console.error('Error:', e))
* // Does not log anything
* ```
*/
function tapErr(result, fn) {
	if (isErr(result)) fn(result.error);
	return result;
}
/**
* Pattern matches on a Result, applying the appropriate handler function.
*
* Provides exhaustive handling of both Ok and Err cases, ensuring you
* handle all possibilities.
*
* @template T - The type of the success value
* @template E - The type of the error value
* @template U - The type of the return value from both handlers
* @param result - The Result to match on
* @param handlers - Object containing `onOk` and `onErr` handler functions
* @returns The return value from whichever handler was called
*
* @example
* ```typescript
* const result: Result<number, string> = ok(42)
*
* const message = match(result, {
*   onOk: (value) => `Success: ${value}`,
*   onErr: (error) => `Failed: ${error}`
* })
* // message = 'Success: 42'
*
* // Transforming to different types
* const status = match(fetchResult, {
*   onOk: (data) => ({ status: 'loaded', data }),
*   onErr: (err) => ({ status: 'error', message: err.message })
* })
* ```
*/
function match(result, handlers) {
	return isOk(result) ? handlers.onOk(result.value) : handlers.onErr(result.error);
}
/**
* Collects an array of Results into a Result containing an array.
*
* If all Results are Ok, returns Ok with an array of all values.
* If any Result is Err, returns the first Err encountered.
*
* @template T - The type of the success values
* @template E - The type of the error value
* @param results - Array of Results to collect
* @returns Ok with array of values if all succeed, or first Err
*
* @example
* ```typescript
* collect([ok(1), ok(2), ok(3)])
* // ok([1, 2, 3])
*
* collect([ok(1), err('first'), err('second')])
* // err('first')
*
* collect([])
* // ok([])
*
* // Useful for validating multiple fields
* const validations = [
*   validateName(name),
*   validateEmail(email),
*   validateAge(age)
* ]
* const allValid = collect(validations)
* ```
*/
function collect(results) {
	const values = [];
	for (const result of results) {
		if (isErr(result)) return result;
		values.push(result.value);
	}
	return ok(values);
}
function all(...results) {
	return collect(results);
}

//#endregion
//#region src/error.ts
/**
* Creates a ResultError with the given code, message, and optional cause.
*
* @template C - The type of error code (inferred from the code parameter)
* @param code - Unique error code identifying the type of error
* @param message - Human-readable error message
* @param cause - Optional original error that caused this error
* @returns A new ResultError object
*
* @example
* ```typescript
* // Basic error
* const error = resultError('NOT_FOUND', 'User not found')
* // { code: 'NOT_FOUND', message: 'User not found' }
*
* // With cause
* try {
*   await fetchUser(id)
* } catch (e) {
*   const error = resultError('FETCH_FAILED', 'Could not fetch user', e as Error)
*   return err(error)
* }
*
* // With typed error codes
* const ApiError = {
*   NotFound: 'NOT_FOUND',
*   Timeout: 'TIMEOUT'
* } as const
*
* const typedError = resultError(ApiError.NotFound, 'Resource not found')
* // typedError.code is typed as 'NOT_FOUND'
* ```
*/
function resultError(code, message, cause) {
	return {
		code,
		message,
		cause
	};
}
/**
* Default error code for unknown or unspecified errors.
*
* Use this when catching exceptions that don't map to a specific error code
* in your domain.
*
* @example
* ```typescript
* import { tryCatch, UNKNOWN_ERROR } from '@mks2508/no-throw'
*
* const result = tryCatch(() => {
*   throw new Error('Unexpected!')
* })
*
* if (isErr(result)) {
*   console.log(result.error.code) // 'UNKNOWN'
* }
*
* // Or provide a specific code
* const result2 = tryCatch(() => {
*   throw new Error('Unexpected!')
* }, 'PARSING_ERROR')
*
* if (isErr(result2)) {
*   console.log(result2.error.code) // 'PARSING_ERROR'
* }
* ```
*/
const UNKNOWN_ERROR = "UNKNOWN";

//#endregion
//#region src/utils.ts
/**
* @fileoverview Utility functions for exception wrapping and Result creation.
* @module @mks2508/no-throw/utils
*/
/**
* Creates a failure Result with a structured ResultError.
*
* This is a convenience function that combines `err()` and `resultError()`
* for creating typed error Results in a single call.
*
* @template C - The type of error code (inferred from the code parameter)
* @param code - Unique error code identifying the type of error
* @param message - Human-readable error message
* @param cause - Optional original error that caused this error
* @returns An Err Result containing the ResultError
*
* @example
* ```typescript
* // Basic usage
* function findUser(id: string): Result<User, ResultError<'NOT_FOUND'>> {
*   const user = db.get(id)
*   if (!user) {
*     return fail('NOT_FOUND', `User ${id} not found`)
*   }
*   return ok(user)
* }
*
* // With error chaining
* async function fetchData(url: string): Promise<Result<Data, ResultError>> {
*   try {
*     const response = await fetch(url)
*     if (!response.ok) {
*       return fail('HTTP_ERROR', `Request failed: ${response.status}`)
*     }
*     return ok(await response.json())
*   } catch (e) {
*     return fail('NETWORK_ERROR', 'Network request failed', e as Error)
*   }
* }
*
* // With typed error codes
* const UserError = {
*   NotFound: 'USER_NOT_FOUND',
*   InvalidEmail: 'INVALID_EMAIL',
*   Suspended: 'USER_SUSPENDED'
* } as const
*
* type UserError = ResultError<ErrorCode<typeof UserError>>
*
* function validateUser(email: string): Result<void, UserError> {
*   if (!email.includes('@')) {
*     return fail(UserError.InvalidEmail, 'Email must contain @')
*   }
*   return ok(undefined)
* }
* ```
*/
function fail(code, message, cause) {
	return err(resultError(code, message, cause));
}
/**
* Converts a Promise that may reject into a Promise that resolves to a Result.
*
* This is useful for integrating with APIs that use exceptions for error handling,
* wrapping them in the Result pattern.
*
* @template T - The type of the resolved value
* @template C - The type of error code (defaults to 'UNKNOWN')
* @param promise - The Promise to wrap
* @param errorCode - Optional error code to use instead of UNKNOWN_ERROR
* @returns A Promise that always resolves to a Result
*
* @example
* ```typescript
* // Basic usage with fetch
* const result = await fromPromise(fetch('/api/users'))
* if (isOk(result)) {
*   const response = result.value
* }
*
* // With custom error code
* const result = await fromPromise(
*   fetch('/api/users'),
*   'NETWORK_ERROR'
* )
*
* // Chaining with other Result operations
* const users = await fromPromise(fetch('/api/users'))
*   .then(r => flatMap(r, async response => {
*     if (!response.ok) return fail('HTTP_ERROR', `Status: ${response.status}`)
*     return fromPromise(response.json(), 'PARSE_ERROR')
*   }))
*
* // Comparing approaches
* // Before (exceptions)
* try {
*   const response = await fetch('/api/users')
*   const data = await response.json()
*   return data
* } catch (e) {
*   console.error(e)
*   return null
* }
*
* // After (Result)
* const result = await fromPromise(fetch('/api/users'), 'FETCH_ERROR')
* if (isErr(result)) {
*   console.error(result.error.message)
*   return null
* }
* const data = await fromPromise(result.value.json(), 'PARSE_ERROR')
* ```
*/
async function fromPromise(promise, errorCode) {
	try {
		return ok(await promise);
	} catch (error) {
		const cause = error instanceof Error ? error : new Error(String(error));
		return err(resultError(errorCode ?? UNKNOWN_ERROR, cause.message, cause));
	}
}
/**
* Wraps a synchronous function that may throw into a function returning Result.
*
* Use this to safely call code that might throw exceptions, converting them
* into Results instead.
*
* @template T - The return type of the function
* @template C - The type of error code (defaults to 'UNKNOWN')
* @param fn - The function to wrap
* @param errorCode - Optional error code to use instead of UNKNOWN_ERROR
* @returns A Result containing either the return value or a ResultError
*
* @example
* ```typescript
* // Wrapping JSON.parse
* const result = tryCatch(() => JSON.parse(jsonString))
* if (isOk(result)) {
*   console.log(result.value)
* } else {
*   console.error('Invalid JSON:', result.error.message)
* }
*
* // With custom error code
* const result = tryCatch(
*   () => JSON.parse(jsonString),
*   'JSON_PARSE_ERROR'
* )
*
* // Wrapping any throwing function
* const result = tryCatch(() => {
*   const value = riskyOperation()
*   return transform(value)
* }, 'OPERATION_FAILED')
*
* // Practical example: safe regex
* function safeRegex(pattern: string): Result<RegExp, ResultError<'INVALID_REGEX'>> {
*   return tryCatch(() => new RegExp(pattern), 'INVALID_REGEX')
* }
*
* const regex = safeRegex('[invalid')
* // regex = err({ code: 'INVALID_REGEX', message: '...', cause: SyntaxError })
* ```
*/
function tryCatch(fn, errorCode) {
	try {
		return ok(fn());
	} catch (error) {
		const cause = error instanceof Error ? error : new Error(String(error));
		return err(resultError(errorCode ?? UNKNOWN_ERROR, cause.message, cause));
	}
}
/**
* Wraps an async function that may throw into a function returning Promise<Result>.
*
* Similar to `tryCatch` but for async functions. The function is immediately
* invoked and its result is wrapped.
*
* @template T - The resolved type of the async function
* @template C - The type of error code (defaults to 'UNKNOWN')
* @param fn - The async function to wrap
* @param errorCode - Optional error code to use instead of UNKNOWN_ERROR
* @returns A Promise that resolves to a Result
*
* @example
* ```typescript
* // Basic usage
* const result = await tryCatchAsync(async () => {
*   const response = await fetch('/api/data')
*   return response.json()
* })
*
* // With custom error code
* const result = await tryCatchAsync(
*   async () => {
*     const data = await fetchData()
*     return processData(data)
*   },
*   'DATA_PROCESSING_ERROR'
* )
*
* // Combining multiple async operations
* const result = await tryCatchAsync(async () => {
*   const user = await db.users.findOne(id)
*   if (!user) throw new Error('User not found')
*
*   const posts = await db.posts.find({ userId: user.id })
*   return { user, posts }
* }, 'FETCH_USER_DATA')
*
* // Difference from fromPromise:
* // - fromPromise: wraps an existing Promise
* // - tryCatchAsync: creates and wraps a new Promise from an async function
*
* // Use fromPromise when you have a Promise:
* const p = someAsyncOperation()
* const r1 = await fromPromise(p, 'ERROR')
*
* // Use tryCatchAsync when you want to define the async logic:
* const r2 = await tryCatchAsync(async () => {
*   // your async logic here
* }, 'ERROR')
* ```
*/
async function tryCatchAsync(fn, errorCode) {
	try {
		return ok(await fn());
	} catch (error) {
		const cause = error instanceof Error ? error : new Error(String(error));
		return err(resultError(errorCode ?? UNKNOWN_ERROR, cause.message, cause));
	}
}

//#endregion
export { UNKNOWN_ERROR, all, collect, err, fail, flatMap, fromPromise, isErr, isOk, map, mapErr, match, ok, resultError, tap, tapErr, tryCatch, tryCatchAsync, unwrap, unwrapOr, unwrapOrElse };
//# sourceMappingURL=index.mjs.map